import { toast } from 'react-toastify'

import { handleError } from './apiUtils'
import { getConfigurationAsync, xKMSClient } from './httpMethods'
import {
  GenericKeyResponseDto,
  SearchKeysResponseDtoSigningKeyEntity,
  SigningKeysApi,
} from './kmsapiclient'

/**
 * Uploads signing keys asynchronously.
 *
 * @param file - The path to the file containing the signing keys.
 * @param keyName - The name of the key to be uploaded.
 * @param aadRevocation - The AAD revocation information.
 * @returns A promise that resolves to a `GenericKeyResponseDto` containing the response data.
 */
export async function uploadSigningKeysAsync(
  file: string,
  keyName: string,
  aadRevocation: string,
): Promise<GenericKeyResponseDto> {
  try {
    const signingKeysApi = new SigningKeysApi(getConfigurationAsync())
    const response = (
      await signingKeysApi.signingKeyUpload(
        file,
        keyName,
        aadRevocation,
        xKMSClient,
      )
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Searches for signing keys based on the provided request.
 *
 * @param request - The request object containing search criteria.
 * @returns A promise that resolves to the search response.
 */
export async function searchSigningKeyAsync(
  request,
): Promise<SearchKeysResponseDtoSigningKeyEntity> {
  try {
    const signingKeysApi = new SigningKeysApi(getConfigurationAsync())
    const response = (
      await signingKeysApi.signingKeysSearch(xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Retrieves a signing key based on the provided request.
 *
 * @param keyId - The ID of the signing key to retrieve.
 * @returns A promise that resolves to the signing key response.
 */
export async function updateSigningKeyAsync(
  keyId: string,
  key,
): Promise<GenericKeyResponseDto> {
  const handleError = (res) => {
    let errorMessage = res.message
    const errorCode =
      res.response && res.response.data && res.response.data.errorCode

    if (errorCode) {
      if (
        errorCode == 420 &&
        confirm(
          'This signing key has been modified by another session.\n\nReload current session?\n\nThe most recent changes will be discarded.',
        )
      ) {
        location.reload()
        return
      } else {
        errorMessage = `Please contact your administrator. (Error Code: ${errorCode}, Status Code: ${res.response.status})`
      }
    }
    toast.error(errorMessage)
  }

  try {
    const signingKeysApi = new SigningKeysApi(getConfigurationAsync())
    const response = (
      await signingKeysApi.signingKeyUpdate(keyId, xKMSClient, key)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Removes a signing key based on the provided request.
 *
 * @param keyId - The ID of the signing key to remove.
 * @returns A promise that resolves to the status of the removal.
 */
export async function removeSigningKeyAsync(request): Promise<string> {
  try {
    const signingKeysApi = new SigningKeysApi(getConfigurationAsync())
    const response = (
      await signingKeysApi.signingKeyDelete(request.keyId, xKMSClient)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}
